/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import {CellEditingStartedEvent, ColDef} from 'ag-grid-community';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {CreateOfflineCanRequestPayload, OfflineCanFrameRequest} from '@ion/core/src/types/services';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import TertiaryButton from '@ion/dls/src/components/buttons/TertiaryButton';
import {ButtonSpinner} from '@ion/dls/src/components/loading/ButtonSpinner';
import Dropdown from '@ion/dls/src/components/menus/Dropdown';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {errorOptions} from '../../../prompts/constants';
import {toggleUserPrompt} from '../../../store/actions/user-prompt';
import CanRequestCheckinConditionsEditor from './CanRequestCheckinConditionsEditor';
import {
  CheckinConditionsDraft,
  EMPTY_CHECKIN_CONDITIONS_DRAFT,
  hasCheckinConditionRowErrors,
  serializeCheckinConditions
} from './CanRequestCheckinConditionUtils';
import {hexPayloadToDecimalCsv, hexTokenToNumber} from './CanRequestConversionUtils';
import {
  CanRequestDraftRow,
  cloneDraftRow,
  createDefaultRow,
  createDefaultVinReadRows,
  createRowId,
  DATA_MAX_BYTES,
  DEFAULT_CAN_SPEED,
  DEFAULT_DLC,
  formatHexPayloadForDisplay,
  hasRowErrors,
  MAX_LABEL_LENGTH,
  resizePayloadToTxLen,
  withErrors
} from './CanRequestCreateRowUtils';
import CanRequestPayloadEditor from './CanRequestPayloadEditor';
import EditableRowsGrid from './EditableRowsGrid';

interface CanRequestCreateViewProps {
  esn: string;
  onCancel: () => void;
  onSubmitted?: (payload: CreateOfflineCanRequestPayload) => Promise<void> | void;
  submitDisabledReason?: string;
}

type RowErrorField = Exclude<keyof CanRequestDraftRow, 'rowId' | 'errors'>;
type RequestDefaultPreset = 'default' | 'vinRead';

const useStyles = makeStyles(() => ({
  root : {
    display       : 'flex',
    flexDirection : 'column',
    flex          : 1,
    minHeight     : 0,
    height        : '100%',
    overflowY     : 'auto'
  },
  gridContainer : {
    display      : 'flex',
    flex         : '1 1 320px',
    minHeight    : 320,
    overflow     : 'hidden',
    marginBottom : '24px'
  },
  title : {
    ...dlsAlt('typography', 'label1'),
    color         : dlsColor('grey1'),
    letterSpacing : '1px',
    marginBottom  : '12px'
  },
  actions : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'flex-end',
    gap            : '12px',
    marginTop      : '12px',
    flexShrink     : 0
  },
  submitButton : {
    minWidth : 180
  },
  submitButtonWrapper : {
    display : 'inline-flex'
  },
  requestDefaultSelect : {
    width : 200
  },
  requestDefaultMenuPaper : {
    '& li.MuiMenuItem-root' : {
      minHeight : 40,
      padding   : '8px 12px'
    },
    '& li.MuiMenuItem-root > div' : {
      padding : '0 !important'
    }
  }
}));

const createDefaultDraftRow = (): CanRequestDraftRow => createDefaultRow(createRowId());
const createZeroDraftRow = (): CanRequestDraftRow => withErrors({
  rowId     : createRowId(),
  txId      : '0',
  canSource : 1,
  txLen     : DEFAULT_DLC,
  rxId      : '0',
  rxMask    : '0',
  canSpeed  : `${DEFAULT_CAN_SPEED}`,
  txPayload : '00-00-00-00-00-00-00-00',
  label     : ''
});
const createDefaultPresetRows = (): CanRequestDraftRow[] => [createZeroDraftRow()];
const createDefaultVinDraftRows = (): CanRequestDraftRow[] => createDefaultVinReadRows([
  createRowId(),
  createRowId(),
  createRowId()
]);

const cloneCanRequestDraftRow = (row: CanRequestDraftRow): CanRequestDraftRow => {
  return cloneDraftRow(row, createRowId());
};

const toInvalidCellClassRule = (field: RowErrorField) => {
  return function({data}) {
    return !!data?.errors?.[field];
  };
};

const resolveCreateCanRequestErrorMessage = (
  submitErrorMessage: string,
  err: any
): string => {
  const apiMessage = err?.response?.data?.response?.errors?.[0]?.description;
  return apiMessage || submitErrorMessage;
};

/**
 * Renders the CAN request create editor with editable frame rows and submit actions.
 *
 * ```typescript
 * <CanRequestCreateView
 *   esn='3272001381'
 *   onCancel={onCancel}
 *   onSubmitted={onSubmitted}
 *   submitDisabledReason={cleanSessionTooltip}
 * />
 * ```
 *
 * @param   esn                  Selected ESN used when building the create request payload.
 * @param   onCancel             Invoked when the user exits create mode.
 * @param   onSubmitted          Invoked with the assembled API payload on submit.
 * @param   submitDisabledReason When set, disables submit and shows this text as its tooltip.
 * @returns The CAN request create view component.
 */
const CanRequestCreateView = ({
  esn,
  onCancel,
  onSubmitted,
  submitDisabledReason = ''
}: CanRequestCreateViewProps) => {
  const dispatch                    = useDispatch();
  const classes                     = useStyles();
  const [rows, setRows]             = useState<CanRequestDraftRow[]>(() => createDefaultPresetRows());
  const [requestDefaultPreset, setRequestDefaultPreset] = useState<RequestDefaultPreset>('default');
  const [submitting, setSubmitting] = useState(false);
  const [checkinConditions, setCheckinConditions] = useState<CheckinConditionsDraft>(EMPTY_CHECKIN_CONDITIONS_DRAFT);

  const labels = translate('vehicle-manager:vehicleHistoryGrid.canRequestCreateDialog', {
    returnObjects : true
  }) || {};

  const {
    autosize = '',
    undo = '',
    redo = '',
    cloneRow = '',
    addRow = '',
    requestDefaults = '',
    default: defaultPreset = '',
    vinRead = '',
    cancel = '',
    submit = '',
    identifier = '',
    channel = '',
    dlc = '',
    rxId = '',
    mask = '',
    canSpeed = '',
    data = '',
    label = '',
    submitSuccess = '',
    submitError = ''
  } = labels || {};

  const presetOptions = useMemo(() => ({
    default : defaultPreset,
    vinRead : vinRead
  }), [defaultPreset, vinRead]);
  const requestDefaultOptions = useMemo(() => ([
    {label: presetOptions.default, value: 'default'},
    {label: presetOptions.vinRead, value: 'vinRead'}
  ]), [presetOptions.default, presetOptions.vinRead]);

  const columnDefs = useMemo<ColDef[]>(() => ([
    {
      headerName     : identifier,
      field          : 'txId',
      cellClassRules : {
        'invalid-cell' : toInvalidCellClassRule('txId')
      }
    },
    {
      headerName       : channel,
      field            : 'canSource',
      cellEditor       : 'agRichSelectCellEditor',
      cellEditorPopup  : true,
      cellEditorParams : {
        values        : [1, 2],
        allowTyping   : false,
        cellHeight    : 42,
        cellRowHeight : 42
      },
      valueFormatter : function({value}) {
        return `CAN ${value}`;
      },
      cellClassRules : {
        'invalid-cell' : toInvalidCellClassRule('canSource')
      }
    },
    {
      headerName       : dlc,
      field            : 'txLen',
      cellEditor       : 'agRichSelectCellEditor',
      cellEditorPopup  : true,
      cellEditorParams : {
        values        : [1, 2, 3, 4, 5, 6, 7, 8],
        allowTyping   : false,
        cellHeight    : 42,
        cellRowHeight : 42
      },
      cellClassRules : {
        'invalid-cell' : toInvalidCellClassRule('txLen')
      }
    },
    {
      headerName     : rxId,
      field          : 'rxId',
      cellClassRules : {
        'invalid-cell' : toInvalidCellClassRule('rxId')
      }
    },
    {
      headerName     : mask,
      field          : 'rxMask',
      cellClassRules : {
        'invalid-cell' : toInvalidCellClassRule('rxMask')
      }
    },
    {
      headerName     : canSpeed,
      field          : 'canSpeed',
      cellClassRules : {
        'invalid-cell' : toInvalidCellClassRule('canSpeed')
      }
    },
    {
      headerName       : label,
      field            : 'label',
      minWidth         : 220,
      cellEditor       : 'agTextCellEditor',
      cellEditorParams : {
        maxLength : MAX_LABEL_LENGTH
      }
    },
    {
      headerName     : data,
      field          : 'txPayload',
      minWidth       : 210,
      flex           : 1,
      cellEditor     : CanRequestPayloadEditor,
      valueFormatter : function({data}) {
        const txLen = Math.max(1, Math.min(Number(data?.txLen || DATA_MAX_BYTES), DATA_MAX_BYTES));
        return formatHexPayloadForDisplay(data?.txPayload || '', txLen);
      },
      cellClassRules : {
        'invalid-cell' : toInvalidCellClassRule('txPayload')
      }
    }
  ]), [canSpeed, channel, data, dlc, identifier, label, mask, rxId]);

  function onRowsChange(nextRows: CanRequestDraftRow[]) {
    const previousById = new Map(rows.map((row) => [row.rowId, row]));
    const normalizedRows = nextRows.map((row) => {
      const previous = previousById.get(row.rowId);
      const nextTxLen = Number(row.txLen);
      const didTxLenChange = Number(previous?.txLen) !== nextTxLen;
      const nextPayload = didTxLenChange
        ? resizePayloadToTxLen(row.txPayload, nextTxLen)
        : row.txPayload;
      return withErrors({
        ...row,
        txPayload : nextPayload
      });
    });
    setRows(normalizedRows);
  }

  function onGridCellEditingStarted(event: CellEditingStartedEvent<CanRequestDraftRow>) {
    if (event?.colDef?.field !== 'txLen') {
      return;
    }

    window.setTimeout(() => {
      const pickerWrapper = document.querySelector(
        '.ag-popup-editor .ag-wrapper.ag-picker-field-wrapper.ag-rich-select-value'
      ) as HTMLElement | null;

      if (pickerWrapper && !pickerWrapper.classList.contains('ag-picker-expanded')) {
        pickerWrapper.click();
      }
    }, 0);
  }

  function onRequestDefaultChange(event: any) {
    const nextPreset = event.target.value as RequestDefaultPreset;
    const nextRows = nextPreset === 'default'
      ? createDefaultPresetRows()
      : createDefaultVinDraftRows();
    setRequestDefaultPreset(nextPreset);
    setRows(nextRows);
  }

  function createDraftRowByPreset(): CanRequestDraftRow {
    return requestDefaultPreset === 'default'
      ? createZeroDraftRow()
      : createDefaultDraftRow();
  }

  function onCheckinConditionsChange(nextCheckinConditions: CheckinConditionsDraft) {
    setCheckinConditions(nextCheckinConditions);
  }

  const submitDisabled = !esn || !rows.length || rows.some(hasRowErrors) ||
    checkinConditions.rows.some(hasCheckinConditionRowErrors) || submitting || Boolean(submitDisabledReason);

  const parseOptionalHexToken = (value: string): number | undefined => {
    const normalized = `${value || ''}`.trim();
    return normalized ? hexTokenToNumber(normalized) : undefined;
  };

  const toFrameRequest = (row: CanRequestDraftRow): OfflineCanFrameRequest => {
    const rxId = parseOptionalHexToken(row.rxId);
    const rxMask = parseOptionalHexToken(row.rxMask);
    return {
      txId      : hexTokenToNumber(row.txId),
      txLen     : Number(row.txLen),
      txPayload : hexPayloadToDecimalCsv(row.txPayload),
      ...(rxId !== undefined ? {rxId} : {}),
      ...(rxMask !== undefined ? {rxMask} : {}),
      canSource : Number(row.canSource),
      canSpeed  : Number(row.canSpeed),
      ...(row.label ? {label: row.label} : {})
    };
  };

  async function onSubmit() {
    if (submitDisabled || !esn) {
      return;
    }
    try {
      setSubmitting(true);
      const condition = serializeCheckinConditions(checkinConditions);
      const payload   = {
        esn,
        ...(condition ? {condition} : {}),
        canRequests : rows.map(toFrameRequest)
      };
      await onSubmitted?.(payload);
      dispatch(toggleUserPrompt({
        message          : submitSuccess,
        actionLabel      : translate('common:dismiss'),
        autoHideDuration : 3000
      }));
      onCancel();
    } catch (err) {
      error(err);
      dispatch(toggleUserPrompt({
        ...errorOptions,
        message       : resolveCreateCanRequestErrorMessage(submitError, err),
        actionLabel   : translate('common:tryAgain'),
        errorState    : true,
        translateText : false
      }));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={classes.root}>
      <EditableRowsGrid rows={rows}
        onRowsChange={onRowsChange}
        columnDefs={columnDefs}
        createRow={createDraftRowByPreset}
        cloneRow={cloneCanRequestDraftRow}
        toolbarLeftContent={(
          <div className={classes.requestDefaultSelect}>
            <Dropdown options={requestDefaultOptions}
              value={requestDefaultPreset}
              autoComplete={false}
              fieldLabel={'Preset'}
              placeholder={requestDefaults || 'Request Defaults'}
              onChange={onRequestDefaultChange}
              MenuProps={{
                anchorOrigin : {
                  vertical   : 'bottom',
                  horizontal : -4
                },
                transformOrigin : {
                  vertical   : 'top',
                  horizontal : 'left'
                },
                getContentAnchorEl : null,
                classes            : {
                  paper : classes.requestDefaultMenuPaper
                }
              }}
              labelAsValue={false} />
          </div>
        )}
        className={classes.gridContainer}
        stopEditingOnBlur
        onCellEditingStarted={onGridCellEditingStarted}
        labels={{
          autosize : autosize,
          undo     : undo,
          redo     : redo,
          clone    : cloneRow,
          addRow   : addRow
        }} />
      <CanRequestCheckinConditionsEditor value={checkinConditions}
        onChange={onCheckinConditionsChange}
        disabled={submitting} />
      <div className={classes.actions}>
        <TertiaryButton onClick={onCancel}>
          {cancel}
        </TertiaryButton>
        <Tooltip arrow
          placement="bottom"
          title={submitDisabledReason}>
          <span className={classes.submitButtonWrapper}>
            <PrimaryButton onClick={onSubmit}
              className={classes.submitButton}
              disabled={submitDisabled}>
              {submitting ? <ButtonSpinner /> : submit}
            </PrimaryButton>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default CanRequestCreateView;
